import { showVoiceRecorder } from '../components/voice-recorder.js';
import { getVoiceSettings, subscribeVoiceSettings, getComposeStyle } from '../../domain/voice-settings.js';

export interface ComposeCallbacks {
  onSubmit: (content: string, source: 'text' | 'voice') => Promise<void>;
}

let callbacks: ComposeCallbacks | null = null;
export function setComposeCallbacks(cb: ComposeCallbacks) {
  callbacks = cb;
}

export function renderFloatingCompose(root: HTMLElement): () => void {
  root.innerHTML = `
    <div class="compose" id="compose-container">
      <button class="compose__pill" type="button" aria-label="メモを追加" id="compose-pill">
        <span class="compose__icon" aria-hidden="true">🎤</span>
        <span class="compose__placeholder">メモる</span>
      </button>
    </div>
    <div class="compose-sheet" id="compose-sheet" hidden>
      <div class="compose-sheet__backdrop" id="compose-backdrop"></div>
      <div class="compose-sheet__panel" role="dialog" aria-label="メモを書く">
        <textarea class="compose-sheet__textarea" id="compose-textarea" rows="4"
                  placeholder="メモる" aria-label="メモ内容"></textarea>
        <div class="compose-sheet__actions">
          <button class="compose-sheet__mic" type="button" id="compose-mic" aria-label="音声で入力">🎤</button>
          <span class="compose-sheet__count" id="compose-count">0 文字</span>
          <button class="compose-sheet__save" type="button" id="compose-save">保存</button>
        </div>
      </div>
    </div>
  `;

  const pill = root.querySelector<HTMLButtonElement>('#compose-pill')!;
  const sheet = root.querySelector<HTMLDivElement>('#compose-sheet')!;
  const backdrop = root.querySelector<HTMLDivElement>('#compose-backdrop')!;
  const textarea = root.querySelector<HTMLTextAreaElement>('#compose-textarea')!;
  const count = root.querySelector<HTMLSpanElement>('#compose-count')!;
  const save = root.querySelector<HTMLButtonElement>('#compose-save')!;

  const container = root.querySelector<HTMLDivElement>('#compose-container')!;
  const applyPillStyle = () => {
    const s = getVoiceSettings();
    const style = getComposeStyle();
    pill.classList.toggle('compose__pill--voice-mode', s.mode === 'auto');
    pill.classList.toggle('compose__pill--fab', style === 'fab');
    container.classList.toggle('compose--fab', style === 'fab');
    const placeholder = pill.querySelector<HTMLSpanElement>('.compose__placeholder');
    if (placeholder) {
      // FAB 時は placeholder は CSS で非表示、aria-label のみ
      placeholder.textContent = s.mode === 'auto' ? '音声モード ON' : 'メモる';
    }
    pill.setAttribute('aria-label', s.mode === 'auto' ? '音声モード — タップで録音' : 'メモる');
  };
  applyPillStyle();
  const unsubscribeSettings = subscribeVoiceSettings(applyPillStyle);

  const open = () => {
    sheet.hidden = false;
    requestAnimationFrame(() => sheet.classList.add('is-open'));
    setTimeout(() => textarea.focus(), 100);
  };
  const close = () => {
    sheet.classList.remove('is-open');
    textarea.value = '';
    count.textContent = '0 文字';
    setTimeout(() => { sheet.hidden = true; }, 200);
  };

  pill.addEventListener('click', async () => {
    const s = getVoiceSettings(); // 都度読む (設定変更後の反映)
    if (s.mode === 'auto') {
      // 音声モード: pill タップで即録音
      const result = await showVoiceRecorder(s.silenceTimeoutMs);
      if (result.text && callbacks) {
        try {
          await callbacks.onSubmit(result.text, 'voice');
        } catch (err) {
          console.error(err);
          alert('保存に失敗しました');
        }
      }
    } else {
      open();
    }
  });
  backdrop.addEventListener('click', close);
  textarea.addEventListener('input', () => {
    count.textContent = `${[...textarea.value].length} 文字`;
  });
  textarea.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      save.click();
    }
  });
  save.addEventListener('click', async () => {
    const content = textarea.value.trim();
    if (!content || !callbacks) return;
    save.disabled = true;
    try {
      await callbacks.onSubmit(content, 'text');
      close();
    } catch (err) {
      console.error(err);
      alert('保存に失敗しました');
    } finally {
      save.disabled = false;
    }
  });

  const mic = root.querySelector<HTMLButtonElement>('#compose-mic')!;
  mic.addEventListener('click', async () => {
    const s = getVoiceSettings();
    const result = await showVoiceRecorder(s.silenceTimeoutMs);
    if (result.text) {
      textarea.value = (textarea.value + (textarea.value ? ' ' : '') + result.text).trim();
      count.textContent = `${[...textarea.value].length} 文字`;
    }
  });

  return () => {
    unsubscribeSettings();
  };
}
